/**
 * Iter 68 — Vendor TOTP 2FA panel.
 *
 * Three stable states the UI renders:
 *   • disabled     — show the "Enable 2FA" button. Clicking it calls /enroll
 *                    and switches to `enrolling`.
 *   • enrolling    — show the QR + a 6-digit input; on confirm, calls
 *                    /activate and (on success) switches to `enabled`.
 *   • enabled      — show status + a "Disable" CTA that also requires a
 *                    fresh 6-digit code to confirm physical possession.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Smartphone, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function VendorTwoFactorPanel() {
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | enrolling | disabling
  const [enrollData, setEnrollData] = useState(null);
  const [code, setCode] = useState("");
  const [working, setWorking] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/vendor/2fa/status").then((res) => res.data);
      setStatus(r);
      // Coming back from a stale `enrolling` stage means the activation
      // already happened — sync.
      if (r.enabled && stage === "enrolling") setStage("idle");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startEnroll = async () => {
    setWorking(true);
    try {
      const r = await api.post("/vendor/2fa/enroll").then((res) => res.data);
      setEnrollData(r);
      setStage("enrolling");
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const activate = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your authenticator app.");
    setWorking(true);
    try {
      await api.post("/vendor/2fa/activate", { code });
      toast.success("Two-factor authentication enabled.");
      setStage("idle");
      setEnrollData(null);
      setCode("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your authenticator app to disable.");
    setWorking(true);
    try {
      await api.post("/vendor/2fa/disable", { code });
      toast.success("Two-factor authentication disabled.");
      setStage("idle");
      setCode("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  if (!status) return null;

  return (
    <Card data-testid="vendor-2fa-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" /> Two-factor authentication
          {status.enabled && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 ml-1">Enabled</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Add a 6-digit code from an authenticator app to every login. We support
          Google Authenticator, Authy, Microsoft Authenticator, and any TOTP-compatible app.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {stage === "idle" && !status.enabled && (
          <div className="flex flex-wrap items-center gap-3">
            <Smartphone className="h-8 w-8 text-neutral-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-neutral-700">
                Two-factor auth is currently <strong>disabled</strong>. Set it up in under a minute.
              </p>
            </div>
            <Button onClick={startEnroll} disabled={working} data-testid="vendor-2fa-enable">
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Enable 2FA
            </Button>
          </div>
        )}

        {stage === "enrolling" && enrollData && (
          <div className="space-y-4" data-testid="vendor-2fa-enrolling">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <img
                src={enrollData.qr_data_url}
                alt="2FA QR code"
                className="border rounded-lg bg-white p-2"
                width={192}
                height={192}
                data-testid="vendor-2fa-qr"
              />
              <div className="space-y-3 flex-1 min-w-0">
                <p className="text-sm">
                  <strong>1.</strong> Open your authenticator app and scan the QR code, or paste this
                  secret manually:
                </p>
                <code className="block text-xs bg-neutral-100 rounded p-2 font-mono break-all" data-testid="vendor-2fa-secret">
                  {enrollData.secret}
                </code>
                <p className="text-sm"><strong>2.</strong> Type the 6-digit code your app shows:</p>
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="font-mono text-lg w-32 text-center tracking-widest"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="vendor-2fa-code"
                  />
                  <Button onClick={activate} disabled={working || code.length !== 6} data-testid="vendor-2fa-activate">
                    {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Activate
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setStage("idle"); setEnrollData(null); setCode(""); }}
                    disabled={working}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {stage === "idle" && status.enabled && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700">
              2FA was activated{" "}
              {status.activated_at ? new Date(status.activated_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "recently"}.
              Each login now requires a 6-digit code from your authenticator app.
            </p>
            <Button variant="outline" onClick={() => setStage("disabling")} data-testid="vendor-2fa-show-disable">
              Disable 2FA
            </Button>
          </div>
        )}

        {stage === "disabling" && (
          <div className="space-y-3" data-testid="vendor-2fa-disabling">
            <p className="text-sm text-neutral-700">
              To disable 2FA, enter your current 6-digit code one last time. This confirms
              you still have access to your authenticator app.
            </p>
            <div className="flex gap-2 items-center">
              <Label htmlFor="vendor-2fa-disable-code" className="sr-only">Code</Label>
              <Input
                id="vendor-2fa-disable-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                className="font-mono text-lg w-32 text-center tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="vendor-2fa-disable-code"
              />
              <Button variant="destructive" onClick={disable} disabled={working || code.length !== 6} data-testid="vendor-2fa-disable">
                {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Disable
              </Button>
              <Button variant="ghost" onClick={() => { setStage("idle"); setCode(""); }} disabled={working}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Iter 69 — Trusted devices list. Only shown when 2FA is enabled.
            Renders one row per browser that's currently allowed to skip
            the TOTP prompt. Single revoke + nuke-all available. */}
        {status?.enabled && <TrustedDevicesSection />}
      </CardContent>
    </Card>
  );
}

function TrustedDevicesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // device_id currently being revoked

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor/2fa/trusted-devices").then((res) => res.data);
      setItems(r.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/vendor/2fa/trusted-devices/${id}`);
      toast.success("Device revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const revokeAll = async () => {
    if (!window.confirm("Revoke every trusted device? You'll need a 6-digit code on every login from any browser until you re-trust them.")) return;
    setBusy("all");
    try {
      const r = await api.post("/vendor/2fa/trusted-devices/revoke-all").then((res) => res.data);
      toast.success(`Revoked ${r.revoked_count} device${r.revoked_count === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  // Hide the section entirely when there's nothing to manage — keeps the
  // panel clean for vendors who never opted in.
  if (!loading && items.length === 0) return null;

  return (
    <div className="border-t border-border pt-4 mt-4 space-y-3" data-testid="trusted-devices-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-sm font-semibold">Trusted devices</h3>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Browsers that skip the 6-digit code for 30 days. Revoke any you don&apos;t recognise.
          </p>
        </div>
        {items.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={revokeAll}
            disabled={busy === "all"}
            className="h-8 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid="trusted-devices-revoke-all"
          >
            {busy === "all" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            Revoke all
          </Button>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-neutral-400 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => {
            const expiresIn = d.expires_at ? new Date(d.expires_at) - Date.now() : 0;
            const daysLeft = Math.max(0, Math.ceil(expiresIn / 86400000));
            const lastUsed = d.last_used_at ? new Date(d.last_used_at).toLocaleString() : "never";
            const ua = (d.user_agent || "").toLowerCase();
            const browserGuess = ua.includes("chrome") ? "Chrome"
              : ua.includes("firefox") ? "Firefox"
              : ua.includes("safari") ? "Safari"
              : ua.includes("edge") ? "Edge"
              : "Browser";
            const osGuess = ua.includes("iphone") || ua.includes("ipad") ? "iOS"
              : ua.includes("android") ? "Android"
              : ua.includes("mac os") ? "macOS"
              : ua.includes("windows") ? "Windows"
              : ua.includes("linux") ? "Linux"
              : "Unknown OS";
            return (
              <li
                key={d.id}
                className="flex items-center gap-3 rounded-xl border border-border bg-white p-3"
                data-testid={`trusted-device-row-${d.id}`}
              >
                <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <Smartphone className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-semibold text-neutral-900 truncate">{browserGuess} · {osGuess}</div>
                  <div className="text-neutral-500 mt-0.5">
                    IP {d.ip || "—"} · last used {lastUsed} · {daysLeft}d left
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => revoke(d.id)}
                  disabled={busy === d.id}
                  className="h-8 px-3 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                  data-testid={`trusted-device-revoke-${d.id}`}
                >
                  {busy === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Revoke"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
